/**
 * help/search — client-side ranking over the help corpus.
 *
 * The corpus ships in the bundle, so search is synchronous and works offline.
 * That is a deliberate trade: it costs bundle size and buys a search box that
 * answers on every keystroke with no server round-trip and no spinner.
 *
 * WHEN THIS STOPS BEING THE RIGHT ANSWER: the help set is expected to get large.
 * The seam is `searchHelp()` — swap its body for a fetch against a server index
 * and every caller keeps working. Nothing outside this file knows how ranking
 * happens. Rough ceiling before that's worth doing: a few hundred articles.
 */

import { articleText, type HelpArticle } from './types';

export interface HelpHit {
  article: HelpArticle;
  score: number;
  /** A short slice of body text around the first match, for the result list. */
  snippet: string;
}

/** Split a query into lowercase words, dropping punctuation and empties. */
function terms(q: string): string[] {
  return q
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .filter((t) => t.length > 1);
}

/**
 * Words so common in this product that matching them says nothing about
 * relevance — every article mentions "tax" and "property". They still count,
 * but at a fraction, so a query like "property tax invoice" ranks on "invoice".
 */
const WEAK = new Set(['the', 'and', 'for', 'you', 'your', 'this', 'that', 'with', 'from', 'tax', 'property', 'appealstack']);

/** Per-field weights. Title beats keywords beats summary beats body. */
const W_TITLE = 12;
const W_KEYWORD = 8;
const W_SUMMARY = 4;
const W_BODY = 1;

function countOccurrences(haystack: string, needle: string): number {
  if (!needle) return 0;
  let n = 0;
  let i = haystack.indexOf(needle);
  while (i !== -1) {
    n += 1;
    i = haystack.indexOf(needle, i + needle.length);
  }
  return n;
}

function scoreOne(a: HelpArticle, ts: string[]): number {
  const title = a.title.toLowerCase();
  const summary = a.summary.toLowerCase();
  const keywords = a.keywords.join(' ').toLowerCase();
  const body = articleText(a).toLowerCase();

  let score = 0;
  let matchedTerms = 0;

  for (const t of ts) {
    const weak = WEAK.has(t) ? 0.15 : 1;
    let termScore = 0;

    // Whole-word title match outranks a substring — "value" should not let
    // "Values and savings" beat an article literally titled "Value".
    if (new RegExp(`\\b${t}\\b`).test(title)) termScore += W_TITLE;
    else if (title.includes(t)) termScore += W_TITLE * 0.5;

    if (keywords.includes(t)) termScore += W_KEYWORD;
    if (summary.includes(t)) termScore += W_SUMMARY;
    termScore += Math.min(countOccurrences(body, t), 4) * W_BODY;

    if (termScore > 0) matchedTerms += 1;
    score += termScore * weak;
  }

  // Require every non-weak term to land somewhere. Without this an "invoice
  // quickbooks" query returns every invoice article, and the one about
  // QuickBooks — the only article that answers the question — is buried.
  const strongTerms = ts.filter((t) => !WEAK.has(t));
  if (strongTerms.length > 1 && matchedTerms < strongTerms.length) {
    score *= 0.35;
  }

  return score;
}

/** Build a ~160-char slice of body text around the first term hit. */
function buildSnippet(a: HelpArticle, ts: string[]): string {
  const text = articleText(a);
  const lower = text.toLowerCase();
  let at = -1;
  for (const t of ts) {
    const i = lower.indexOf(t);
    if (i !== -1 && (at === -1 || i < at)) at = i;
  }
  if (at === -1) return a.summary;
  const start = Math.max(0, at - 60);
  const slice = text.slice(start, start + 170).trim();
  return (start > 0 ? '…' : '') + slice + (start + 170 < text.length ? '…' : '');
}

/**
 * Rank the corpus against a query. Empty/whitespace query returns [] — the
 * caller shows the browse-by-category view instead, which is a different screen,
 * not a zero-result search.
 */
export function searchHelp(query: string, corpus: HelpArticle[], limit = 20): HelpHit[] {
  const ts = terms(query);
  if (ts.length === 0) return [];

  const hits: HelpHit[] = [];
  for (const article of corpus) {
    const score = scoreOne(article, ts);
    if (score <= 0) continue;
    hits.push({ article, score, snippet: buildSnippet(article, ts) });
  }

  hits.sort((x, y) => y.score - x.score || x.article.title.localeCompare(y.article.title));
  return hits.slice(0, limit);
}
